import axios from 'axios';
import { formatPostObject } from '../util/formatPostObject';
import { CompanyFormData, FormData, MSPFormData } from '../types';
import { FORM_TYPES } from '../consts';
import { getUserCheckEndpoint } from '../util/getExperimentVersion';
import { getCookie } from '../util/cookies';
import addExperimentVersionToUrl from '../util/successUrlHandler';

export function postContactFormDataService(postObject: object, optout: boolean, textareaExistsStateVariable: boolean) {
  const formattedPostObject = formatPostObject(postObject, optout, textareaExistsStateVariable);
  const determinedContactPostEndpoint = (() => {
    const form = document.getElementById('form');
    const isLegacyPartner = form.dataset?.type === 'partner';
    if (isLegacyPartner) {
      return 'SendPartnersDataAsync';
    } else if (window.LPFormData.formType === FORM_TYPES.PARTNER) {
      return 'SendPartnersInterest';
    } else {
      return 'SendAsync';
    }
  })();
  return axios
    .post(`/lpapi/form/${determinedContactPostEndpoint}`, formattedPostObject)
    .catch(() => {
      return false;
    })
    .then((data) => {
      return !!data;
    });
}

export const postTrialFormDataService = async (data: FormData | CompanyFormData) => {
  try {
    const response = await axios.post('/lpapi/trial/SubmitFormData', data, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    return {
      success: false,
      code: error?.response?.data?.code || 0,
    };
  }
};

export const postMSPTrialFormDataService = async (data: MSPFormData) => {
  try {
    const response = await axios.post('/lpapi/trial/SubmitMspData', data, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    return {
      success: false,
      code: error?.response?.data?.code || 0,
    };
  }
};

export const postTeamsTrialFormDataService = async (data: FormData) => {
  try {
    const response = await axios.post('/lpapi/trial/SubmitTeamsFormData', data, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    return {
      success: false,
      code: error?.response?.data?.code || 0,
    };
  }
};

export const postTrialFormDataServiceLegacy = async (data: FormData, successUrl: string) => {
  try {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = addExperimentVersionToUrl(successUrl);

    const emailInput = document.createElement('input');
    emailInput.type = 'hidden';
    emailInput.name = 'email';
    emailInput.value = data.email;
    form.appendChild(emailInput);

    const contactPermission = document.createElement('input');
    contactPermission.type = 'hidden';
    contactPermission.name = 'contactPermission';
    contactPermission.value = data.contactPermission.toString();
    form.appendChild(contactPermission);

    const marketingOptOutShown = document.createElement('input');
    marketingOptOutShown.type = 'hidden';
    marketingOptOutShown.name = 'marketingOptOutShown';
    marketingOptOutShown.value = data.marketingOptOutShown.toString();
    form.appendChild(marketingOptOutShown);

    document.body.appendChild(form);
    form.submit();
  } catch (error) {
    return {
      success: false,
      code: 500,
    };
  }
};

export const postFamiliesTrialFormDataService = async (data: FormData) => {
  try {
    const response = await axios.post('/lpapi/trial/SubmitFamiliesFormData', data, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    return {
      success: false,
      code: error?.response?.data?.code || 0,
    };
  }
};

export function getSiteCoreContent() {
  const LPFormData = window.LPFormData;
  const contentId = typeof LPFormData === 'object' ? LPFormData.contentId : '{B4F20BDD-552E-4827-878C-71E0977D3ACD}';
  const cookieValue = getCookie('lastpass#lang');
  const langPathSection = cookieValue || 'en';
  if (LPFormData.formType === FORM_TYPES.TRIAL || LPFormData.formType === FORM_TYPES.DIRECTBUY) {
    return axios.get(`/${langPathSection}/lpapi/trial/getfields/${contentId}`);
  } else {
    return axios.get(`/${langPathSection}/lpapi/form/fields/${contentId}`);
  }
}

export function getSiteCoreModalContent(contentId: string) {
  const cookieValue = getCookie('lastpass#lang');
  const langPathSection = cookieValue || 'en';
  return axios.get(`/${langPathSection}/lpapi/trial/getfields/${contentId}`);
}

export const getEmail = async () => {
  try {
    const response = await axios.get('/lpapi/trial/gettrialemail');
    return response.data;
  } catch (error) {
    return { success: false };
  }
};

export const checkBusinessUser = async (data: { flowType: string; trialLength: number; trialSku: string }) => {
  try {
    const response = await axios.post('/lpapi/trial/EmailCheckBusinessUser', data);
    return response.data;
  } catch (error) {
    return {
      success: false,
      code: error?.response?.data?.code || 0,
    };
  }
};

export const checkUserValidity = async (data: {
  email: string;
  trialLength: number;
  trialSku: string;
  flowType?: string;
}) => {
  try {
    const response = await axios.post(`/lpapi/trial/${getUserCheckEndpoint()}`, data);
    return response.data;
  } catch (error) {
    return {
      success: false,
      code: error?.response?.data?.code || 0,
      correctedEmail: error?.response?.data?.correctedEmail,
    };
  }
};

export const retainFormData = async (data: FormData) => {
  try {
    const response = await axios.post('/lpapi/trial/retainformdata', data);
    return response.data;
  } catch (error) {
    return { success: false };
  }
};

export const getFormData = async () => {
  try {
    const response = await axios.get('/lpapi/trial/gettrialdatainfo');
    return response.data;
  } catch (error) {
    return { success: false };
  }
};
